import Api from '@/models/Api'
import Opportunity from '@/models/Opportunity'
import OpportunityActivity from '@/models/OpportunityActivity'
import SystemDashboardWidget from '@/models/SystemDashboardWidget'
import User from '@/models/User'
import { buildColorPool } from '@/models/Util'
import router from '@/Routes'
import SystemtModule from '@/store/SystemModule'
import { h } from 'vue'
import { getModule } from 'vuex-module-decorators'

export default class DisneyMlbSummary extends SystemDashboardWidget {
  public title: string = 'MLB Summary'

  public key: string = 'mlb-summary-disney'

  public view: string = 'table'

  public noPagination: boolean = true

  public noTotal: boolean = true

  public size: any = {
    lg: 12,
    md: 12,
  }

  public fields: any = [
    {
      key: 'label',
      label: 'Label',
      class: 'text-center align-middle',
      show: true,
      thClass: 'align-middle text-center',
      sortable: true,
      filter: true,
    },
    // revenue
    {
      key: 'revenue',
      label: 'Revenue',
      class: 'text-center align-middle',
      show: true,
      thClass: 'align-middle text-center',
      type: 'currency_abbreviate',
      sortable: true,
    },
    // percentage_to_mg
    {
      key: 'percentage_to_mg',
      label: '% to MG',
      class: 'text-center align-middle',
      show: true,
      thClass: 'align-middle text-center',
      type: 'percentage',
      formatter: (value: any) => `${Number(value).toFixed(2)}%`,
      sortable: true,
    },
  ]

  public dataSourceRequest: any = null

  constructor(args: any = null) {
    super()

    if (args && args.dimensions) {
      this.dimensions = args.dimensions
    }

    if (args && args.metrics) {
      this.metrics = args.metrics
    }

    if (args && args.filters) {
      this.filters = Object.assign(this.filters, args.filters)
    }
    if (args && args.internalFilters) {
      this.internalFilters = Object.assign(this.internalFilters, args.internalFilters)
    }

    if (args && args.rightCol) {
      this.rightCol = args.rightCol
    }

    if (args && args.leftCol) {
      this.leftCol = args.leftCol
    }

    if (args && args.size) {
      this.size = args.size
    }

    if (args && args.title) {
      this.title = args.title
    }

    if (args && args.key) {
      this.key = args.key
    }

    if (args && args.noPagination) {
      this.noPagination = args.noPagination
    }

    if (args && args.updateFields) {
      this.fields = args.updateFields(this.fields)
    }

    if (args && args.internalQuery) {
      this.internalQuery = args.internalQuery
    }

    if (args && args.dataSourceRequest) {
      this.dataSourceRequest = args.dataSourceRequest
    }

    if (args && args.noPagination) {
      this.noPagination = args.noPagination
    }
    if (args && args.perPage) {
      this.perPage = args.perPage
    }

    if (args && args.fields) {
      this.fields = args.fields
    }

    if (args && args.noTotal) {
      this.noTotal = args.noTotal
    }

    if (args && args.allowAll) {
      this.allowAll = args.allowAll
    }
    if (args && args.bordered) {
      this.bordered = args.bordered
    }

    if (args && args.cellSlots) {
      this.cellSlots = args.cellSlots
    }

    if (args && args.internalQueryPush) {
      this.internalQuery.push(args.internalQueryPush)
    }
  }

  public internalQuery = ['is:this_week']

  public internalFilters = {
    region: 'all',
    product: 'all',
  }

  public local_user: User = new User()

  public auth_user: any = getModule(SystemtModule).user

  public get filterToQuery() {
    let query = []

    if (this.filters.product !== 'all') {
      query.push(`product:${this.filters.product}`)
    }

    if (this.filters.region !== 'all') {
      query.push(`region:${this.filters.region}`)
    }

    return query
  }

  public get payload() {
    const query = [...new Set([...this.query, ...this.internalQuery, ...this.filterToQuery])]

    return {
      query,
      ...this.pagination,
    }
  }

  public async dataSource() {
    // @ts-ignore
    let api = new Api()
    return api
      .get(`disney-dashboards/progress/${this.filters.company_id}`)
      .then((response: any) => response.data.result)
      .catch((err: any) => ({ data: [] }))
  }

  public cellSlots: any[] = [
    {
      key: 'label',
      injectData: true,
      component: (data: any) =>
        h('div', { class: 'd-flex align-items-center justify-content-center' }, [
          h('div', {
            style: {
              backgroundColor: data.item.color,
              width: '12px',
              height: '12px',
              borderRadius: '2px',
              marginRight: '6px',
              flexShrink: '0',
            },
          }),
          h('span', data.item.label),
          h('sup', String(data.index + 1)),
        ]),
    },
  ]

  public footerCellSlots: any[] = []

  public get notes(): string {
    if (!this.data || !this.data.length) return ''

    const items = (this.data as any[])
      .map(
        (row: any, i: number) =>
          `<sup>${i + 1}</sup>&nbsp;<strong>${row.label}:</strong>&nbsp;${row.footnote}`,
      )
      .join('&nbsp;&nbsp;&nbsp;&nbsp;')

    return `<small style="color:#6b7280;">${items}</small>`
  }

  public get customData() {
    if (!this.data || !(this.data as any[]).length) return ''

    const barStages = this.data.filter((row: any) => Number(row.bar_to_mg ?? 0) > 0) as any[]

    const barTotal = barStages.reduce(
      (acc: number, row: any) => acc + Math.max(0, Number(row.bar_to_mg ?? 0)),
      0,
    )
    const barRemaining = Math.max(0, 100 - barTotal)
    console.log('Bar Total:', barTotal, 'Bar Remaining:', barRemaining)

    const segments = barStages
      .filter((row: any) => Number(row.bar_to_mg ?? 0) > 0)
      .map((row: any) => {
        const width = Math.max(0, Number(row.bar_to_mg))
        const displayPct = `${width.toFixed(2)}%`
        const label = displayPct
        return `<td title="${row.label}: ${displayPct}" style="width:${width}%;height:28px;background-color:${row.color};padding:0 4px;text-align:center;vertical-align:middle;font-size:11px;font-weight:bold;color:#fff;white-space:nowrap;overflow:hidden;">${label}</td>`
      })
      .join('')

    const remainingSegment = barRemaining > 0
      ? `<td style="width:${barRemaining}%;height:28px;background-color:#f3f4f6;padding:0 4px;text-align:center;vertical-align:middle;font-size:11px;color:#9ca3af;border:1px solid #e5e7eb;white-space:nowrap;overflow:hidden;">${barRemaining.toFixed(
        2,
      )}%</td>`
      : ''

    return `<table style="width:100%;margin-top:14px;border-collapse:collapse;table-layout:fixed;border-radius:4px;overflow:hidden;"><tr>${segments}${remainingSegment}</tr></table>`
  }
}
